import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X, ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { INQUIRY } from "../config/content";
import { saveLead } from "../lib/api";

const TOTAL = INQUIRY.totalSteps;
const EASE = [0.22, 1, 0.36, 1];
const emptyAnswers = { experience: "", problems: [], markets: [], mode: "", timing: "", capital: "" };
const emptyContact = { name: "", phone: "", email: "", city: "", consent: true };

function getTracking() {
  const p = new URLSearchParams(window.location.search);
  return {
    source: window.location.pathname,
    page_url: window.location.href,
    utm_source: p.get("utm_source") || "",
    utm_medium: p.get("utm_medium") || "",
    utm_campaign: p.get("utm_campaign") || "",
  };
}

function OptionCard({ selected, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left rounded-xl border px-4 py-3.5 sm:px-5 sm:py-4 text-sm sm:text-base font-semibold transition-colors duration-200 flex items-center justify-between gap-3 ${
        selected
          ? "bg-[#22C55E]/10 border-[#22C55E] text-white shadow-[0_0_26px_-6px_rgba(34,197,94,0.55)]"
          : "bg-[#070A08] border-[#22C55E]/[0.18] text-[#A7B3AA] hover:border-[#22C55E]/50"
      }`}
    >
      <span>{children}</span>
      <span
        className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
          selected ? "bg-[#22C55E] border-[#22C55E]" : "border-[#22C55E]/30"
        }`}
        aria-hidden="true"
      >
        {selected && <Check className="w-3 h-3 text-[#04100A]" strokeWidth={4} />}
      </span>
    </button>
  );
}

export default function LeadModal({ open, onClose }) {
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [answers, setAnswers] = useState(emptyAnswers);
  const [contact, setContact] = useState(emptyContact);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [leadKey, setLeadKey] = useState("");
  const tracking = useMemo(getTracking, []);
  const timerRef = useRef(null);
  const stateRef = useRef({ answers, leadKey });
  stateRef.current = { answers, leadKey };

  useEffect(() => {
    if (open && !leadKey) setLeadKey(crypto.randomUUID());
  }, [open, leadKey]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Autosave partial progress whenever answers change
  useEffect(() => {
    if (!open || !leadKey || !answers.experience) return;
    const t = setTimeout(() => {
      saveLead({ lead_key: leadKey, ...stateRef.current.answers, ...tracking }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, step, open, leadKey]);

  const goNext = () => { setDir(1); setStep((s) => Math.min(s + 1, TOTAL - 1)); };
  const goBack = () => { setDir(-1); setStep((s) => Math.max(s - 1, 0)); };

  const pickSingle = (field, value, autoAdvance = true) => {
    setAnswers((a) => ({ ...a, [field]: value }));
    if (autoAdvance) {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(goNext, 300);
    }
  };

  const pickDual = (field, value, otherField) => {
    setAnswers((a) => {
      const next = { ...a, [field]: value };
      if (!a[field] && value && next[otherField]) {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(goNext, 350);
      }
      return next;
    });
  };

  const toggleMulti = (field, value, max) => {
    setAnswers((a) => {
      const cur = a[field];
      if (cur.includes(value)) return { ...a, [field]: cur.filter((v) => v !== value) };
      if (max && cur.length >= max) return a;
      return { ...a, [field]: [...cur, value] };
    });
  };

  const setContactField = (k, v) => {
    setContact((c) => ({ ...c, [k]: v }));
    setErrors((e) => ({ ...e, [k]: "" }));
  };

  const validateContact = () => {
    const e = {};
    if (contact.name.trim().length < 2) e.name = "Please enter your name";
    const digits = contact.phone.replace(/\D/g, "");
    if (!/^[6-9]\d{9}$/.test(digits)) e.phone = "Enter a valid 10-digit mobile number";
    if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) e.email = "Enter a valid email or leave it blank";
    if (contact.city.trim().length < 2) e.city = "Please enter your city";
    if (!contact.consent) e.consent = "Please agree so we can call you about your check-up";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const reset = () => {
    setStep(0);
    setDir(1);
    setAnswers(emptyAnswers);
    setContact(emptyContact);
    setErrors({});
    setApiError("");
    setLeadKey("");
  };

  const onSubmitContact = async (ev) => {
    ev.preventDefault();
    if (!validateContact()) return;
    setSubmitting(true);
    setApiError("");
    try {
      await saveLead({
        lead_key: leadKey,
        ...answers,
        ...contact,
        phone: contact.phone.replace(/\D/g, ""),
        ...tracking,
      });
      sessionStorage.setItem("marubazu_lead", JSON.stringify({
        name: contact.name.trim(),
        city: contact.city.trim(),
        experience: answers.experience,
        problems: answers.problems,
      }));
      clearTimeout(timerRef.current);
      document.body.style.overflow = "";
      onClose();
      setTimeout(reset, 300);
      navigate("/thank-you");
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    const { answers: a, leadKey: k } = stateRef.current;
    if (k && a.experience) {
      saveLead({ lead_key: k, ...a, ...tracking }).catch(() => {});
    }
    clearTimeout(timerRef.current);
    onClose();
    setTimeout(reset, 300);
  };

  const variants = {
    enter: (d) => ({ x: reduce ? 0 : d * 48, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: reduce ? 0 : d * -48, opacity: 0 }),
  };
  const stepTransition = reduce ? { duration: 0 } : { duration: 0.32, ease: EASE };

  const renderQuestionStep = (cfg, index) => {
    if (cfg.type === "dual") {
      const bothChosen = answers[cfg.parts[0].id] && answers[cfg.parts[1].id];
      return (
        <div data-testid="inquiry-step-4">
          {cfg.parts.map((part) => (
            <div key={part.id} className="mb-5">
              <p className="text-xs font-bold uppercase tracking-wider text-[#FACC15] mb-2">{part.label}</p>
              <div className="space-y-2">
                {part.options.map((opt) => (
                  <OptionCard
                    key={opt}
                    selected={answers[part.id] === opt}
                    onClick={() => pickDual(part.id, opt, cfg.parts.find((p) => p.id !== part.id).id)}
                    testid={`inquiry-${part.id}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {opt}
                  </OptionCard>
                ))}
              </div>
            </div>
          ))}
          <NextButton disabled={!bothChosen} onClick={goNext} />
        </div>
      );
    }
    if (cfg.type === "multi") {
      const selected = answers[cfg.id];
      return (
        <div data-testid={`inquiry-step-${index + 1}`}>
          <div className="space-y-2">
            {cfg.options.map((opt) => (
              <OptionCard
                key={opt}
                selected={selected.includes(opt)}
                onClick={() => toggleMulti(cfg.id, opt, cfg.max)}
                testid={`inquiry-${cfg.id}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {opt}
              </OptionCard>
            ))}
          </div>
          <div className="mt-5 flex items-center justify-between gap-4">
            {cfg.max ? <p className="text-xs text-[#6B7A70]">{selected.length}/{cfg.max} selected</p> : <span />}
            <NextButton disabled={selected.length === 0} onClick={goNext} />
          </div>
        </div>
      );
    }
    return (
      <div data-testid={`inquiry-step-${index + 1}`}>
        <div className="space-y-2">
          {cfg.options.map((opt) => (
            <OptionCard
              key={opt}
              selected={answers[cfg.id] === opt}
              onClick={() => pickSingle(cfg.id, opt)}
              testid={`inquiry-${cfg.id}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {opt}
            </OptionCard>
          ))}
        </div>
        {cfg.optional && (
          <button
            type="button"
            onClick={goNext}
            data-testid="inquiry-skip-capital"
            className="mt-4 text-sm text-[#6B7A70] hover:text-white underline underline-offset-4 transition-colors"
          >
            Skip this question
          </button>
        )}
      </div>
    );
  };

  const renderContact = () => (
    <form onSubmit={onSubmitContact} noValidate data-testid="inquiry-step-6">
      <div className="space-y-3.5">
        <div>
          <label htmlFor="inq-name" className="block text-xs font-bold uppercase tracking-wider text-[#A7B3AA] mb-1.5">Your name</label>
          <input
            id="inq-name"
            data-testid="inquiry-name-input"
            type="text"
            value={contact.name}
            onChange={(e) => setContactField("name", e.target.value)}
            placeholder="Your full name"
            className="w-full rounded-xl bg-[#070A08] border border-[#22C55E]/[0.18] focus:border-[#22C55E]/60 outline-none px-4 py-3.5 text-white text-sm placeholder:text-[#6B7A70] transition-colors"
          />
          {errors.name && <p className="mt-1 text-xs text-[#EF4444]" data-testid="inquiry-name-error">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="inq-phone" className="block text-xs font-bold uppercase tracking-wider text-[#A7B3AA] mb-1.5">Mobile number</label>
          <div className="flex rounded-xl bg-[#070A08] border border-[#22C55E]/[0.18] focus-within:border-[#22C55E]/60 transition-colors overflow-hidden">
            <span className="flex items-center px-4 text-sm font-bold text-[#FACC15] border-r border-[#22C55E]/[0.18] select-none">+91</span>
            <input
              id="inq-phone"
              data-testid="inquiry-phone-input"
              type="tel"
              inputMode="numeric"
              maxLength={10}
              value={contact.phone}
              onChange={(e) => setContactField("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="10-digit mobile number"
              className="w-full bg-transparent outline-none px-4 py-3.5 text-white text-sm placeholder:text-[#6B7A70]"
            />
          </div>
          {errors.phone && <p className="mt-1 text-xs text-[#EF4444]" data-testid="inquiry-phone-error">{errors.phone}</p>}
        </div>
        <div>
          <label htmlFor="inq-email" className="block text-xs font-bold uppercase tracking-wider text-[#A7B3AA] mb-1.5">Email address <span className="text-[#6B7A70] normal-case font-medium">(optional)</span></label>
          <input
            id="inq-email"
            data-testid="inquiry-email-input"
            type="email"
            value={contact.email}
            onChange={(e) => setContactField("email", e.target.value)}
            placeholder="you@example.com"
            className="w-full rounded-xl bg-[#070A08] border border-[#22C55E]/[0.18] focus:border-[#22C55E]/60 outline-none px-4 py-3.5 text-white text-sm placeholder:text-[#6B7A70] transition-colors"
          />
          {errors.email && <p className="mt-1 text-xs text-[#EF4444]" data-testid="inquiry-email-error">{errors.email}</p>}
        </div>
        <div>
          <label htmlFor="inq-city" className="block text-xs font-bold uppercase tracking-wider text-[#A7B3AA] mb-1.5">City</label>
          <input
            id="inq-city"
            data-testid="inquiry-city-input"
            type="text"
            value={contact.city}
            onChange={(e) => setContactField("city", e.target.value)}
            placeholder="Your city"
            className="w-full rounded-xl bg-[#070A08] border border-[#22C55E]/[0.18] focus:border-[#22C55E]/60 outline-none px-4 py-3.5 text-white text-sm placeholder:text-[#6B7A70] transition-colors"
          />
          {errors.city && <p className="mt-1 text-xs text-[#EF4444]" data-testid="inquiry-city-error">{errors.city}</p>}
        </div>
        <label className="flex items-start gap-3 cursor-pointer select-none pt-1" data-testid="inquiry-consent">
          <input
            type="checkbox"
            checked={contact.consent}
            onChange={(e) => setContactField("consent", e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[#22C55E]"
            data-testid="inquiry-consent-checkbox"
          />
          <span className="text-xs sm:text-sm text-[#A7B3AA] leading-relaxed">{INQUIRY.contact.consentLabel}</span>
        </label>
        {errors.consent && <p className="text-xs text-[#EF4444]" data-testid="inquiry-consent-error">{errors.consent}</p>}
        {apiError && <p className="text-sm text-[#EF4444]" data-testid="inquiry-api-error">{apiError}</p>}
        <button
          type="submit"
          disabled={submitting}
          data-testid="inquiry-submit-button"
          className="btn-primary group relative w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#22C55E] hover:bg-[#16A34A] disabled:opacity-60 text-[#04100A] font-extrabold text-base px-8 py-4 transition-colors overflow-hidden"
        >
          <span className="relative z-10 flex items-center gap-2">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {INQUIRY.contact.submit}
          </span>
          {!submitting && <ArrowRight className="w-5 h-5 relative z-10 arrow-slide" />}
          <span className="shine" aria-hidden="true" />
        </button>
      </div>
    </form>
  );

  const currentCfg = step < INQUIRY.steps.length ? INQUIRY.steps[step] : null;
  const heading = currentCfg ? currentCfg.question : INQUIRY.contact.question;
  const helper = currentCfg ? currentCfg.helper : INQUIRY.contact.helper;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="lead-modal"
        >
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={close} aria-hidden="true" />
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="relative w-full sm:max-w-lg bg-[#0F1712] border border-[#22C55E]/30 rounded-t-3xl sm:rounded-3xl flex flex-col h-[94dvh] sm:h-auto sm:max-h-[88vh] overflow-hidden shadow-[0_0_80px_-20px_rgba(34,197,94,0.4)]"
            role="dialog"
            aria-modal="true"
            data-lenis-prevent
          >
            <button onClick={close} aria-label="Close" data-testid="lead-modal-close" className="absolute top-4 right-4 w-9 h-9 rounded-full border border-[#22C55E]/25 flex items-center justify-center text-[#A7B3AA] hover:text-white transition-colors z-10">
              <X className="w-4 h-4" />
            </button>
            <div className="shrink-0 px-6 sm:px-8 pt-6 sm:pt-8">
              <div className="flex items-center gap-3 mb-2 pr-10">
                {step > 0 && (
                  <button
                    onClick={goBack}
                    aria-label="Back"
                    data-testid="inquiry-back-button"
                    className="w-8 h-8 rounded-full border border-[#22C55E]/25 flex items-center justify-center text-[#A7B3AA] hover:text-white hover:border-[#22C55E]/60 transition-colors shrink-0"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                )}
                <span className="font-heading text-[#FACC15] text-sm tracking-[0.25em]" data-testid="inquiry-step-indicator">
                  {INQUIRY.eyebrow} · {step + 1}/{TOTAL}
                </span>
              </div>
              <div className="h-1 rounded-full bg-[#17231B] overflow-hidden" data-testid="inquiry-progress">
                <motion.div
                  className="h-full bg-[#22C55E] rounded-full"
                  animate={{ width: `${((step + 1) / TOTAL) * 100}%` }}
                  transition={reduce ? { duration: 0 } : { duration: 0.4, ease: EASE }}
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain px-6 sm:px-8 pt-5 pb-6 sm:pb-8" data-lenis-prevent>
              <AnimatePresence mode="wait" custom={dir} initial={false}>
                <motion.div
                  key={step}
                  custom={dir}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={stepTransition}
                >
                  <h2 className="font-heading uppercase text-white text-3xl leading-tight tracking-wide" data-testid="inquiry-question">{heading}</h2>
                  <p className="mt-1.5 mb-5 text-sm text-[#A7B3AA] leading-relaxed" data-testid="inquiry-helper">{helper}</p>
                  {currentCfg ? renderQuestionStep(currentCfg, step) : renderContact()}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function NextButton({ disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid="inquiry-next-button"
      className="ml-auto inline-flex items-center gap-2 rounded-full bg-[#22C55E] hover:bg-[#16A34A] disabled:opacity-40 disabled:cursor-not-allowed text-[#04100A] font-extrabold text-sm px-6 py-3 transition-colors"
    >
      Next <ArrowRight className="w-4 h-4" />
    </button>
  );
}
